import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, Swords, ChevronUp, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import ImageUploader from "../components/ImageUploader";
import SiteContentEditor from "../admin/SiteContentEditor";
import FaqEditor from "../admin/FaqEditor";
import GalleryEditor from "../admin/GalleryEditor";
import SponsorsEditor from "../admin/SponsorsEditor";
import MessagesView from "../admin/MessagesView";
import GamesEditor from "../admin/GamesEditor";
import { useGames } from "../context/GamesContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const EMPTY_POST = { title: "", excerpt: "", content: "", image: "", tag: "News" };
const EMPTY_PLAYER = {
  ign: "",
  role: "",
  game: "",
  secondary_games: [],
  image: "",
  image_position: "50% 50%",
  bio: "",
  country: "",
  rank: "",
  twitter: "",
  twitch: "",
  youtube: "",
  instagram: "",
};
const EMPTY_TOURNAMENT = {
  name: "",
  game: "",
  date: "",
  prize_pool: "",
  status: "upcoming",
  registration_url: "",
  result: "",
  image: "",
};

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const taCls = "bg-[#121212] border-axe-border rounded-xl text-white p-4";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { activeGames } = useGames();
  const [confirm, ConfirmModal] = useConfirm();
  const [apps, setApps] = useState([]);
  const [posts, setPosts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tourneys, setTourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state (shared)
  const [editorKind, setEditorKind] = useState(null); // "post" | "player" | "tournament"

  const movePlayer = async (index, direction) => {
    const newOrder = [...players];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newOrder.length) return;
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    setPlayers(newOrder);
    try {
      await api.put("/players-reorder", { ids: newOrder.map((p) => p.id) });
    } catch (e) {
      toast.error("Reorder failed");
    }
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Matches modal state
  const [matchesFor, setMatchesFor] = useState(null);
  const [matchForm, setMatchForm] = useState(null);
  const [matchSaving, setMatchSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [a, b, p, t] = await Promise.all([
        api.get("/applications"),
        api.get("/blog"),
        api.get("/players"),
        api.get("/tournaments"),
      ]);
      setApps(a.data);
      setPosts(b.data);
      setPlayers(p.data);
      setTourneys(t.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  function openEditor(kind, record = null) {
    setEditorKind(kind);
    setEditing(record);
    if (kind === "post") setForm(record ? { title: record.title, excerpt: record.excerpt, content: record.content, image: record.image, tag: record.tag } : EMPTY_POST);
    if (kind === "player") setForm(record ? { ...EMPTY_PLAYER, ...record } : { ...EMPTY_PLAYER, game: activeGames[0]?.key || "" });
    if (kind === "tournament") setForm(record ? { ...EMPTY_TOURNAMENT, ...record, date: record.date?.slice(0, 16) || "" } : { ...EMPTY_TOURNAMENT, game: activeGames[0]?.key || "" });
  }
  function closeEditor() {
    setEditorKind(null);
    setEditing(null);
    setForm({});
  }

  async function saveEditor(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editorKind === "post") {
        if (editing) {
          await api.put(`/blog/${editing.id}`, form);
          toast.success("Post updated");
        } else {
          await api.post("/blog", form);
          toast.success("Post published");
        }
      } else if (editorKind === "player") {
        if (editing) {
          await api.put(`/players/${editing.id}`, form);
          toast.success("Player updated");
        } else {
          await api.post("/players", form);
          toast.success("Player added");
        }
      } else if (editorKind === "tournament") {
        const payload = { ...form, date: form.date ? new Date(form.date).toISOString() : new Date().toISOString() };
        if (editing) {
          await api.put(`/tournaments/${editing.id}`, payload);
          toast.success("Tournament updated");
        } else {
          await api.post("/tournaments", payload);
          toast.success("Tournament added");
        }
      }
      closeEditor();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(kind, id) {
    const label = { post: "post", player: "player", tournament: "tournament" }[kind];
    if (!(await confirm(`Delete this ${label}?`))) return;
    try {
      const url = kind === "post" ? `/blog/${id}` : kind === "player" ? `/players/${id}` : `/tournaments/${id}`;
      await api.delete(url);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function setAppStatus(id, status) {
    try {
      await api.patch(`/applications/${id}`, { status });
      setApps((cur) => cur.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function saveMatch(e) {
    e.preventDefault();
    if (!matchesFor || !matchForm) return;
    setMatchSaving(true);
    try {
      const payload = {
        team_a: matchForm.team_a,
        team_b: matchForm.team_b,
        score_a: matchForm.score_a === "" || matchForm.score_a == null ? null : Number(matchForm.score_a),
        score_b: matchForm.score_b === "" || matchForm.score_b == null ? null : Number(matchForm.score_b),
        status: matchForm.status || "scheduled",
        played_at: matchForm.played_at ? new Date(matchForm.played_at).toISOString() : null,
        notes: matchForm.notes || "",
      };
      let updated;
      if (matchForm._editId) {
        const { data } = await api.put(`/tournaments/${matchesFor.id}/matches/${matchForm._editId}`, payload);
        updated = data;
        toast.success("Match updated");
      } else {
        const { data } = await api.post(`/tournaments/${matchesFor.id}/matches`, payload);
        updated = data;
        toast.success("Match added");
      }
      setTourneys((cur) => cur.map((x) => (x.id === updated.id ? updated : x)));
      setMatchesFor(updated);
      setMatchForm(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setMatchSaving(false);
    }
  }

  async function deleteMatch(mid) {
    if (!matchesFor) return;
    if (!(await confirm("Delete this match?"))) return;
    try {
      await api.delete(`/tournaments/${matchesFor.id}/matches/${mid}`);
      const updated = { ...matchesFor, matches: (matchesFor.matches || []).filter((m) => m.id !== mid) };
      setMatchesFor(updated);
      setTourneys((cur) => cur.map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="admin-dashboard" className="text-white">
      <ConfirmModal />
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Control Room
            </div>
            <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl">Admin</h1>
            {user?.email && (
              <div className="mt-2 text-sm text-neutral-400" data-testid="admin-email-display">
                Signed in as {user.email}
              </div>
            )}
          </div>
          <button
            onClick={() => { logout(); toast.success("Logged out"); }}
            className="inline-flex items-center gap-2 rounded-full border border-axe-border px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-surface"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-10">
        <Tabs defaultValue="applications">
          <TabsList className="bg-axe-surface border border-axe-border rounded-full p-1 h-auto flex-wrap gap-1 overflow-x-auto no-scrollbar max-w-full">
            {[
              { v: "applications", l: `Apps (${apps.length})`, t: "admin-tab-applications" },
              { v: "players", l: `Players (${players.length})`, t: "admin-tab-players" },
              { v: "games", l: `Games (${activeGames.length})`, t: "admin-tab-games" },
              { v: "tournaments", l: `Tournaments (${tourneys.length})`, t: "admin-tab-tournaments" },
              { v: "blog", l: `Blog (${posts.length})`, t: "admin-tab-blog" },
              { v: "content", l: "Content", t: "admin-tab-content" },
              { v: "faq", l: "FAQ", t: "admin-tab-faq" },
              { v: "gallery", l: "Gallery", t: "admin-tab-gallery" },
              { v: "sponsors", l: "Sponsors", t: "admin-tab-sponsors" },
              { v: "messages", l: "Messages", t: "admin-tab-messages" },
            ].map((x) => (
              <TabsTrigger
                key={x.v}
                value={x.v}
                data-testid={x.t}
                className="rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-bold data-[state=active]:bg-axe-purple data-[state=active]:text-white whitespace-nowrap"
              >
                {x.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* APPLICATIONS */}
          <TabsContent value="applications" className="mt-8">
            {loading ? <div className="text-neutral-400">Loading…</div> : apps.length === 0 ? (
              <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No applications yet.</div>
            ) : (
              <div className="rounded-2xl border border-axe-border bg-axe-surface overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-axe-border hover:bg-transparent">
                      {["IGN","Discord","Age","Region","Game","Rank","Status","Actions"].map((h) => (
                        <TableHead key={h} className="text-axe-tangerine uppercase text-[11px] tracking-[0.25em]">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apps.map((a) => (
                      <TableRow key={a.id} className="border-axe-border" data-testid={`app-row-${a.id}`}>
                        <TableCell className="font-semibold">{a.ign}</TableCell>
                        <TableCell>{a.discord_id}</TableCell>
                        <TableCell>{a.age}</TableCell>
                        <TableCell>{a.region}</TableCell>
                        <TableCell className="uppercase text-xs">{a.primary_game}</TableCell>
                        <TableCell>{a.current_rank}</TableCell>
                        <TableCell>
                          <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold ${
                            a.status === "accepted" ? "bg-axe-tangerine text-black"
                            : a.status === "rejected" ? "bg-red-500/20 text-red-300 border border-red-500/40"
                            : "bg-axe-purple/15 text-axe-purple border border-axe-purple/40"
                          }`}>{a.status}</span>
                        </TableCell>
                        <TableCell className="space-x-3">
                          <button onClick={() => setAppStatus(a.id, "accepted")} className="text-[10px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`app-accept-${a.id}`}>Accept</button>
                          <button onClick={() => setAppStatus(a.id, "rejected")} className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-300 hover:underline" data-testid={`app-reject-${a.id}`}>Reject</button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* PLAYERS */}
          <TabsContent value="players" className="mt-8">
            <div className="flex justify-end mb-5">
              <button onClick={() => openEditor("player")} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="admin-new-player">
                <Plus size={14} /> New Player
              </button>
            </div>
            {players.length === 0 ? (
              <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No players yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {players.map((p, idx) => (
                  <div key={p.id} className="rounded-2xl border border-axe-border bg-axe-surface overflow-hidden" data-testid={`admin-player-${p.id}`}>
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img src={p.image} alt={p.ign} className="h-full w-full object-cover" />
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-axe-purple/90 text-white">{p.game}</div>
                      <div className="absolute top-2 right-2 flex flex-col gap-1">
                        <button onClick={() => movePlayer(idx, "up")} disabled={idx === 0} className="p-1 rounded-lg bg-black/60 text-white hover:bg-black/80 disabled:opacity-20" title="Move up">
                          <ChevronUp size={14} />
                        </button>
                        <button onClick={() => movePlayer(idx, "down")} disabled={idx === players.length - 1} className="p-1 rounded-lg bg-black/60 text-white hover:bg-black/80 disabled:opacity-20" title="Move down">
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="font-display font-bold text-lg">{p.ign}</div>
                      <div className="text-[11px] uppercase tracking-[0.3em] text-axe-tangerine font-bold mt-0.5">{p.role}</div>
                      <div className="mt-3 flex gap-3">
                        <button onClick={() => openEditor("player", p)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`admin-player-edit-${p.id}`}>
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => deleteItem("player", p.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`admin-player-delete-${p.id}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TOURNAMENTS */}
          <TabsContent value="tournaments" className="mt-8">
            <div className="flex justify-end mb-5">
              <button onClick={() => openEditor("tournament")} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="admin-new-tournament">
                <Plus size={14} /> New Tournament
              </button>
            </div>
            {tourneys.length === 0 ? (
              <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No tournaments yet.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {tourneys.map((t) => (
                  <div key={t.id} className="rounded-2xl border border-axe-border bg-axe-surface overflow-hidden flex" data-testid={`admin-tournament-${t.id}`}>
                    {t.image && <img src={t.image} alt="" className="w-32 object-cover hidden sm:block" />}
                    <div className="flex-1 p-4">
                      <div className="flex gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 rounded-full bg-axe-purple/20 text-axe-purple border border-axe-purple/40">{t.game}</span>
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5 rounded-full bg-axe-tangerine/20 text-axe-tangerine border border-axe-tangerine/40">{t.status}</span>
                      </div>
                      <div className="mt-2 font-display font-bold text-lg leading-tight">{t.name}</div>
                      <div className="mt-1 text-xs text-neutral-500">{new Date(t.date).toLocaleString()}</div>
                      {t.prize_pool && <div className="mt-1 text-xs text-neutral-400">Prize: {t.prize_pool}</div>}
                      <div className="mt-3 flex gap-3 flex-wrap">
                        <button onClick={() => openEditor("tournament", t)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`admin-tournament-edit-${t.id}`}>
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => setMatchesFor(t)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-purple hover:underline" data-testid={`admin-tournament-matches-${t.id}`}>
                          <Swords size={12} /> Matches ({(t.matches || []).length})
                        </button>
                        <button onClick={() => deleteItem("tournament", t.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`admin-tournament-delete-${t.id}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-8">
            <div className="flex justify-end mb-5">
              <button onClick={() => openEditor("post")} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="admin-new-post">
                <Plus size={14} /> New Post
              </button>
            </div>
            {posts.length === 0 ? (
              <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No posts yet.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {posts.map((p) => (
                  <div key={p.id} className="rounded-2xl border border-axe-border bg-axe-surface overflow-hidden flex" data-testid={`admin-post-${p.id}`}>
                    <img src={p.image} alt="" className="w-28 object-cover hidden sm:block" />
                    <div className="flex-1 p-4">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-axe-purple font-bold">{p.tag}</div>
                      <div className="mt-1 font-display font-bold text-lg leading-tight">{p.title}</div>
                      <div className="mt-1 text-xs text-neutral-500 line-clamp-2">{p.excerpt}</div>
                      <div className="mt-3 flex gap-3">
                        <button onClick={() => openEditor("post", p)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`admin-post-edit-${p.id}`}>
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => deleteItem("post", p.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`admin-post-delete-${p.id}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="mt-8">
            <SiteContentEditor />
          </TabsContent>
          <TabsContent value="games" className="mt-8">
            <GamesEditor />
          </TabsContent>
          <TabsContent value="faq" className="mt-8">
            <FaqEditor />
          </TabsContent>
          <TabsContent value="gallery" className="mt-8">
            <GalleryEditor />
          </TabsContent>
          <TabsContent value="sponsors" className="mt-8">
            <SponsorsEditor />
          </TabsContent>
          <TabsContent value="messages" className="mt-8">
            <MessagesView />
          </TabsContent>
        </Tabs>
      </section>

      {editorKind && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-editor-modal">
          <form onSubmit={saveEditor} className="relative w-full max-w-2xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5 my-10">
            <button type="button" onClick={closeEditor} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close" data-testid="admin-editor-close">
              <X size={18} />
            </button>
            <h3 className="font-display font-black text-2xl">
              {editing ? "Edit " : "New "}
              {editorKind === "post" ? "Post" : editorKind === "player" ? "Player" : "Tournament"}
            </h3>

            {/* POST FORM */}
            {editorKind === "post" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Title</Label>
                  <Input required value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="post-title" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Tag</Label>
                    <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                      <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="post-tag"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-axe-surface border-axe-border text-white">
                        {["News", "Tournament", "Roster", "Community"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Thumbnail</Label>
                    <div className="mt-2"><ImageUploader value={form.image || ""} onChange={(url) => setForm({ ...form, image: url })} aspect="aspect-video" testId="post-image-uploader" /></div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Excerpt</Label>
                  <Textarea required value={form.excerpt || ""} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`mt-2 ${taCls} min-h-[80px]`} data-testid="post-excerpt" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Content</Label>
                  <Textarea required value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`mt-2 ${taCls} min-h-[180px]`} data-testid="post-content" />
                </div>
              </>
            )}

            {/* PLAYER FORM */}
            {editorKind === "player" && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">IGN</Label>
                    <Input required value={form.ign || ""} onChange={(e) => setForm({ ...form, ign: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="player-ign" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Role</Label>
                    <Input required value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Duelist" data-testid="player-role" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Game</Label>
                    <Select value={form.game} onValueChange={(v) => setForm({ ...form, game: v, secondary_games: (form.secondary_games || []).filter((g) => g !== v) })}>
                      <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="player-game"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-axe-surface border-axe-border text-white">
                        {activeGames.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Rank</Label>
                    <Input value={form.rank || ""} onChange={(e) => setForm({ ...form, rank: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Immortal 3" data-testid="player-rank" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Country</Label>
                    <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="US" data-testid="player-country" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Portrait (4:5) · drag the focal dot to choose what stays in view</Label>
                  <div className="mt-2 max-w-xs">
                    <ImageUploader
                      value={form.image || ""}
                      onChange={(url) => setForm({ ...form, image: url })}
                      aspect="aspect-[4/5]"
                      testId="player-image-uploader"
                      focal={form.image_position || "50% 50%"}
                      onFocalChange={(pos) => setForm({ ...form, image_position: pos })}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Bio</Label>
                  <Textarea value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`mt-2 ${taCls} min-h-[100px]`} data-testid="player-bio" />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { k: "twitter", label: "Twitter URL" },
                    { k: "twitch", label: "Twitch URL" },
                    { k: "youtube", label: "YouTube URL" },
                    { k: "instagram", label: "Instagram URL" },
                  ].map((s) => (
                    <div key={s.k}>
                      <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">{s.label}</Label>
                      <Input value={form[s.k] || ""} onChange={(e) => setForm({ ...form, [s.k]: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://…" data-testid={`player-${s.k}`} />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Also plays (secondary games)</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {activeGames.map((g) => {
                      const disabled = g.key === form.game;
                      const checked = (form.secondary_games || []).includes(g.key);
                      return (
                        <button
                          key={g.key}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            const cur = new Set(form.secondary_games || []);
                            if (checked) cur.delete(g.key);
                            else cur.add(g.key);
                            setForm({ ...form, secondary_games: [...cur] });
                          }}
                          data-testid={`player-secondary-${g.key}`}
                          className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold border transition-colors ${
                            disabled
                              ? "opacity-40 cursor-not-allowed border-axe-border"
                              : checked
                              ? "bg-axe-tangerine border-axe-tangerine text-black drop-shadow-[0_0_10px_rgba(255,140,0,0.35)]"
                              : "bg-axe-bg border-axe-border text-neutral-300 hover:text-white"
                          }`}
                        >
                          {g.label}{disabled ? " · primary" : ""}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Player will appear in every selected game's roster tab.
                  </div>
                </div>
              </>
            )}

            {/* TOURNAMENT FORM */}
            {editorKind === "tournament" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Name</Label>
                  <Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="tournament-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Game</Label>
                    <Select value={form.game} onValueChange={(v) => setForm({ ...form, game: v })}>
                      <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="tournament-game"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-axe-surface border-axe-border text-white">
                        {activeGames.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                        <SelectItem value="multi">Multi-title</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="tournament-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-axe-surface border-axe-border text-white">
                        {["upcoming", "live", "completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Date & Time</Label>
                    <Input required type="datetime-local" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="tournament-date" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Prize Pool</Label>
                    <Input value={form.prize_pool || ""} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="$25,000" data-testid="tournament-prize" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Banner Image</Label>
                  <div className="mt-2"><ImageUploader value={form.image || ""} onChange={(url) => setForm({ ...form, image: url })} aspect="aspect-video" testId="tournament-image-uploader" /></div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Registration URL</Label>
                  <Input value={form.registration_url || ""} onChange={(e) => setForm({ ...form, registration_url: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://…" data-testid="tournament-url" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Result (if completed)</Label>
                  <Input value={form.result || ""} onChange={(e) => setForm({ ...form, result: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="AXE — 3rd place" data-testid="tournament-result" />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3">
              <button type="button" onClick={closeEditor} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="editor-save">
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {matchesFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="admin-matches-modal">
          <div className="relative w-full max-w-3xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 my-10">
            <button type="button" onClick={() => { setMatchesFor(null); setMatchForm(null); }} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close" data-testid="admin-matches-close">
              <X size={18} />
            </button>
            <div className="text-xs uppercase tracking-[0.3em] text-axe-tangerine font-bold">Matches</div>
            <h3 className="font-display font-black text-2xl mt-2">{matchesFor.name}</h3>

            <div className="mt-6 space-y-3">
              {(matchesFor.matches || []).length === 0 ? (
                <div className="text-neutral-500 border border-axe-border rounded-xl p-6 text-center">No matches yet.</div>
              ) : (
                matchesFor.matches.map((m) => (
                  <div key={m.id} className="rounded-xl border border-axe-border bg-axe-bg p-4 flex flex-wrap items-center gap-4" data-testid={`admin-match-${m.id}`}>
                    <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full ${
                      m.status === "live" ? "bg-red-500 text-white"
                      : m.status === "completed" ? "bg-axe-purple/80 text-white"
                      : "bg-axe-tangerine/20 text-axe-tangerine border border-axe-tangerine/40"
                    }`}>{m.status}</span>
                    <div className="flex-1 font-display font-bold">
                      {m.team_a} <span className="text-neutral-500 mx-2">{m.score_a ?? "—"} · {m.score_b ?? "—"}</span> {m.team_b}
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setMatchForm({
                        team_a: m.team_a, team_b: m.team_b,
                        score_a: m.score_a ?? "", score_b: m.score_b ?? "",
                        status: m.status, notes: m.notes || "",
                        played_at: m.played_at ? m.played_at.slice(0, 16) : "",
                        _editId: m.id,
                      })} className="text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`admin-match-edit-${m.id}`}>Edit</button>
                      <button onClick={() => deleteMatch(m.id)} className="text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`admin-match-delete-${m.id}`}>Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {matchForm ? (
              <form onSubmit={saveMatch} className="mt-6 rounded-xl border border-axe-purple/40 bg-axe-bg p-5 space-y-4" data-testid="match-form">
                <div className="text-[10px] uppercase tracking-[0.3em] text-axe-purple font-bold">
                  {matchForm._editId ? "Edit match" : "New match"}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Team A</Label>
                    <Input required value={matchForm.team_a} onChange={(e) => setMatchForm({ ...matchForm, team_a: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="match-team-a" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Team B</Label>
                    <Input required value={matchForm.team_b} onChange={(e) => setMatchForm({ ...matchForm, team_b: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="match-team-b" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Score A</Label>
                    <Input type="number" value={matchForm.score_a} onChange={(e) => setMatchForm({ ...matchForm, score_a: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="match-score-a" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Score B</Label>
                    <Input type="number" value={matchForm.score_b} onChange={(e) => setMatchForm({ ...matchForm, score_b: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="match-score-b" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Status</Label>
                    <Select value={matchForm.status} onValueChange={(v) => setMatchForm({ ...matchForm, status: v })}>
                      <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="match-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-axe-surface border-axe-border text-white">
                        {["scheduled", "live", "completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Played At</Label>
                    <Input type="datetime-local" value={matchForm.played_at} onChange={(e) => setMatchForm({ ...matchForm, played_at: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="match-played-at" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Notes</Label>
                    <Input value={matchForm.notes} onChange={(e) => setMatchForm({ ...matchForm, notes: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Map: Lotus" data-testid="match-notes" />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setMatchForm(null)} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
                  <button type="submit" disabled={matchSaving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="match-save">
                    {matchSaving ? "Saving…" : matchForm._editId ? "Update" : "Add"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-6 flex justify-end">
                <button onClick={() => setMatchForm({ team_a: "", team_b: "", score_a: "", score_b: "", status: "scheduled", played_at: "", notes: "", _editId: null })} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="admin-new-match">
                  <Plus size={14} /> Add Match
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
